import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  autodialIcon,
  browserCallingIcon,
  chevronLeft,
  fileIcon,
  whatsappIcon,
  infoCircle,
  // alertTriangleIcon,
} from "../../utility-functions/assetLoader";
import {
  calcPrice,
  currencyFormatter,
  getBasePrice,
  getOrgURLParam,
  proratedCharge,
  sentenceCase,
} from "../../utility-functions";
import { JService } from "../api-service/ApiService";
import classes from "./Cart.module.css";
import CartItem from "./CartItem";
import {
  CustomTooltip,
  InsufficientCreditsModal,
  PaymentMethodModal,
} from "../design/components/customComponents";
import { useSelector } from "react-redux";
import AnimationPage from "../shared/animation-page/AnimationPage";
import { enqueueSnackbar } from "notistack";
import { generateProformaInvoicePdf } from "./GenerateProformaInvoicePDF";
import ChoosePlanModal from "./Modals/ChoosePlanModal";

const licensesInfo = {
  plan: { description: "Full access to all features included in the plan." },
  viewer: {
    description:
      "Access to all plan features except making and receiving calls.",
  },
  AUTODIAL: {
    icon: autodialIcon(),
    description:
      "Automatically dial a pre-set list of phone numbers, save time and manual effort.",
  },
  "BROWSER CALLING": {
    icon: browserCallingIcon(),
    description:
      "Make and receive calls directly in your browser, no phone needed.",
  },
  "WHATSAPP BUSINESS": {
    icon: whatsappIcon(),
    description:
      "Send messages and manage customer chats through a shared WhatsApp inbox.",
  },
};

const formatVnType = (type) => {
  switch (type) {
    case "MOBILE":
      return "Mobile number";
    case "TOLLFREE":
      return "Toll-free number";
    case "STANDARD":
      return "Standard number";
    default:
      return type;
  }
};

const Cart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { payload, from } = location.state || { payload: null, from: null };
  const reduxCredits = useSelector((state) => state?.account?.value?.credits);
  const currency = reduxCredits?.currency === "USD" ? "$" : "₹";
  const whatsappAPIStatus = useSelector(
    (state) => state?.whatsappBusiness?.value?.status || "PENDING",
  );

  //States
  const [selectedLicenses, setSelectedLicenses] = useState(
    payload
      ? [
          {
            type: payload.type?.toLowerCase(),
            feature_id: payload.feature_id ?? null,
            quantity: payload.quantity,
          },
        ]
      : [],
  );
  const [applicableAddons, setApplicableAddons] = useState([]);
  const [billingDetails, setBillingDetails] = useState(null);
  const [plansData, setPlansData] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [showChoosePlanModal, setShowChoosePlanModal] = useState(false);
  const [originalPlanName, setOriginalPlanName] = useState(null);
  const [paymentMethodModalVisible, setPaymentMethodModalVisible] =
    useState(false);
  const [insufficientCreditsModalVisible, setInsufficientCreditsModalVisible] =
    useState(false);

  const primaryItem = selectedLicenses.length > 0 ? selectedLicenses[0] : null;

  //Handlers
  const backBtnHandler = () => {
    navigate("/billing");
  };

  const handleUpdateQuantity = (type, quantity, featureId = null) => {
    setSelectedLicenses((prev) => {
      const existingIndex = prev.findIndex((item) =>
        item.type === "feature"
          ? item.feature_id === featureId
          : item.type === type,
      );

      if (existingIndex > -1) {
        const newArray = [...prev];
        newArray[existingIndex] = { ...newArray[existingIndex], quantity };
        return newArray;
      } else {
        const newItem = { type, quantity };
        if (featureId) newItem.feature_id = featureId;
        return [...prev, newItem];
      }
    });
  };

  const getQuantity = (type, featureId = null) => {
    const item = selectedLicenses?.find((i) =>
      i.type === "feature" ? i.feature_id === featureId : i.type === type,
    );
    return item ? +item.quantity : 0;
  };

  const handleContinueToPayment = async () => {
    if (from !== "reactivate") {
      setPaymentMethodModalVisible(true);
      return;
    }

    let url;
    let payload;

    const getFeatureQty = (addonName) => {
      const addon = applicableAddons?.find((a) => a.name === addonName);
      return addon ? getQuantity("feature", addon.id) : 0;
    };
    url = `/api/v1/subscriptions/reactivate-subscription/${getOrgURLParam()}`;
    payload = {
      plan: billingDetails?.plan?.name,
      plan_quantity: getQuantity("plan"),
      viewer_quantity: getQuantity("viewer"),
      bb_quantity: getFeatureQty("BROWSER CALLING"),
      autodial_quantity: getFeatureQty("AUTODIAL"),
    };
    if (whatsappAPIStatus === "ACTIVE") {
      payload.whatsapp_quantity = getFeatureQty("WHATSAPP BUSINESS");
    }

    setShowLoader(true);
    try {
      const res = await JService.post(url, payload);
      if (!res?.success) throw new Error(res?.message);
      else {
        const checkoutUrl = res?.checkout_url;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        } else {
          navigate("/billing");
        }
      }
    } catch (err) {
      console.error("Error confirming payment:", err);
      enqueueSnackbar(err?.message || "Failed to proceed for payment", {
        variant: "error",
      });
    } finally {
      setShowLoader(false);
    }
  };

  const purchaseLicenseHandler = async (payWith) => {
    setPaymentMethodModalVisible(false);
    setInsufficientCreditsModalVisible(false);

    const licensesPayload = selectedLicenses
      .filter((l) => Number(l.quantity) > 0)
      .map((l) => {
        const item = { type: l.type, quantity: Number(l.quantity) };
        if (l.type === "feature" && l.feature_id) {
          item.feature_id = l.feature_id;
        }
        return item;
      });

    const url = `/api/v1/subscriptions/purchase-licenses/${getOrgURLParam()}`;
    const payload = {
      licenses: licensesPayload,
    };

    if (payWith === "credits") payload.pay_with_credits = true;
    else if (payWith === "card") payload.pay_with_card = true;

    setShowLoader(true);
    try {
      const res = await JService.post(url, payload);

      if (res?.message === "Insufficient balance" && payWith === "credits") {
        setInsufficientCreditsModalVisible(true);
        return;
      }

      if (!res?.success) {
        throw new Error(res?.message);
      }

      const checkoutUrl = res?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        navigate("/billing");
      }
    } catch (err) {
      if (err?.message === "Insufficient balance" && payWith === "credits") {
        setInsufficientCreditsModalVisible(true);
        return;
      }
      console.error("Error purchasing licenses:", err);
      enqueueSnackbar(err?.message || "Failed to purchase licenses", {
        variant: "error",
      });
    } finally {
      setShowLoader(false);
    }
  };

  const handleDownloadProformaInvoice = async () => {
    setShowLoader(true);
    const planChanged =
      from === "reactivate" && billingDetails?.plan?.name !== originalPlanName;
    let proformaItems = selectedLicenses
      ?.filter((l) => Number(l.quantity) > 0)
      ?.filter((l) => {
        if (l.type === "feature" && l.feature_id) {
          const addon = applicableAddons?.find((a) => a.id === l.feature_id);
          if (
            addon?.name === "WHATSAPP BUSINESS" &&
            whatsappAPIStatus !== "ACTIVE"
          ) {
            return false;
          }
        }
        return true;
      })
      ?.map((l) => {
        const item = { type: l.type, quantity: Number(l.quantity) };
        if (l.type === "feature" && l.feature_id) {
          item.feature_id = l.feature_id;
        }
        if (l.type === "plan" && planChanged) {
          item.plan = billingDetails?.plan?.name;
        }
        return item;
      });

    if (from === "reactivate") {
      const vnItems = (billingDetails?.numbers ?? [])
        ?.filter((vn) => !vn.is_included_in_plan && Number(vn.total) > 0)
        ?.map((vn) => ({
          type: "virtual_number",
          quantity: Number(vn.total),
          vn_country: vn.location,
          vn_type: vn.number_type,
        }));
      proformaItems = [...(proformaItems ?? []), ...vnItems];
    }
    const url = `/api/v1/subscriptions/proforma-invoice/${getOrgURLParam()}`;
    JService.post(url, JSON.stringify({ items: proformaItems }))
      .then((res) => {
        if (!res?.success || !res?.proforma_invoice) {
          throw new Error(
            res?.message || "Failed to generate proforma invoice",
          );
        }
        generateProformaInvoicePdf(res.proforma_invoice);
      })
      .catch((err) => {
        console.error("Proforma invoice error:", err);
        enqueueSnackbar(err?.message || "Could not generate proforma invoice", {
          variant: "error",
        });
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const changePlanHandler = (type, billingCycle) => {
    const targetPlanName =
      type === "PROFESSIONAL" ? "Professional" : "Standard";
    const targetBillingCycle = billingCycle.toUpperCase();
    const matchedPlan = plansData?.find(
      (p) =>
        p?.name?.split("-")[0] === targetPlanName &&
        p?.billing_cycle === targetBillingCycle,
    );
    if (matchedPlan) {
      setBillingDetails((prev) => ({
        ...prev,
        plan: {
          ...prev.plan,
          name: matchedPlan.name,
          billing_cycle: matchedPlan.billing_cycle,
          price: matchedPlan.price,
          discounted_price: matchedPlan.discounted_price,
        },
      }));
    }
    setShowChoosePlanModal(false);
  };

  const handleOpenChangePlanModal = () => {
    if (plansData?.length === 0) {
      JService.get(`/api/v1/subscriptions/plans/${getOrgURLParam()}`)
        .then((res) => {
          if (!res?.success) throw new Error(res?.message);
          setPlansData(res?.data ?? []);
        })
        .catch((err) => {
          console.error("Error fetching plans:", err);
          enqueueSnackbar(err?.message || "Failed to fetch plans", {
            variant: "error",
          });
        });
    }
    setShowChoosePlanModal(true);
  };

  //Effects
  useEffect(() => {
    setShowLoader(true);
    Promise.allSettled([
      JService.get(`/api/v1/subscriptions/retrieve-applicable-addons/${getOrgURLParam()}`),
      JService.get(`/api/v1/subscriptions/billing-details/${getOrgURLParam()}`),
    ])
      .then(([addonsResult, billingResult]) => {
        if (addonsResult.status === "fulfilled") {
          const addonsRes = addonsResult.value;
          if (!addonsRes?.success) {
            console.error(
              "Error retrieving applicable addons:",
              addonsRes?.message,
            );
            enqueueSnackbar(
              addonsRes?.message || "Could not retrieve applicable addons",
              { variant: "error" },
            );
          } else {
            setApplicableAddons(addonsRes?.data ?? []);
          }
        } else {
          console.error(
            "Error retrieving applicable addons:",
            addonsResult.reason,
          );
          enqueueSnackbar(
            addonsResult.reason?.message ||
              "Could not retrieve applicable addons",
            { variant: "error" },
          );
        }

        if (billingResult.status === "fulfilled") {
          const billingRes = billingResult.value;
          if (!billingRes?.success) {
            console.error(
              "Error retrieving billing details:",
              billingRes?.message,
            );
            enqueueSnackbar(
              billingRes?.message || "Could not retrieve billing details",
              { variant: "error" },
            );
          } else {
            setBillingDetails(billingRes?.data);
            setOriginalPlanName(billingRes?.data?.plan?.name);
          }
        } else {
          console.error(
            "Error retrieving billing details:",
            billingResult.reason,
          );
          enqueueSnackbar(
            billingResult.reason?.message ||
              "Could not retrieve billing details",
            { variant: "error" },
          );
        }
      })
      .finally(() => {
        setShowLoader(false);
      });
  }, []);

  useEffect(() => {
    if (
      from === "reactivate" &&
      billingDetails &&
      Object.keys(billingDetails)?.length > 0
    ) {
      setSelectedLicenses((prev) => {
        const filtered = prev.filter(
          (item) => item.type !== "plan" && item.type !== "viewer",
        );
        return [
          ...filtered,
          {
            type: "plan",
            quantity: billingDetails?.plan?.licenses?.total,
          },
          {
            type: "viewer",
            quantity: billingDetails?.viewers?.licenses?.total,
          },
        ];
      });
    }
    if (from === "reactivate" && applicableAddons?.length > 0) {
      setSelectedLicenses((prev) => {
        const filtered = prev.filter((item) => item.type !== "feature");
        return [
          ...filtered,
          ...applicableAddons?.map((addon) => ({
            type: "feature",
            feature_id: addon.id,
            quantity: addon?.licenses?.total ?? 0,
          })),
        ];
      });
    }
  }, [from, billingDetails, applicableAddons]);

  const renderAddonItem = (addon) => {
    if (addon?.name === "WHATSAPP BUSINESS" && whatsappAPIStatus !== "ACTIVE")
      return null;
    const qty = getQuantity("feature", addon.id);
    const name =
      addon.name === "WHATSAPP BUSINESS"
        ? "WhatsApp Business"
        : sentenceCase(addon.name);
    return (
      <CartItem
        key={`addon-${addon.id}`}
        icon={licensesInfo[addon.name]?.icon}
        name={name}
        description={licensesInfo[addon.name]?.description}
        price={Number(addon.price)}
        discountedPrice={Number(addon.discounted_price)}
        quantity={qty}
        onIncrement={() => handleUpdateQuantity("feature", qty + 1, addon.id)}
        onDecrement={() =>
          handleUpdateQuantity("feature", qty > 0 ? qty - 1 : 0, addon.id)
        }
        currency={currency}
        from={from}
        assignedLicenses={addon?.licenses?.total - addon?.licenses?.free}
      />
    );
  };

  const renderLicenseItem = (licenseType, data) => {
    if (!data) return null;
    const qty = getQuantity(licenseType);
    const billingCycle = sentenceCase(data?.billing_cycle);
    const name =
      licenseType === "plan"
        ? data.name?.includes("Professional")
          ? `Professional plan - ${billingCycle}`
          : `Standard plan - ${billingCycle}`
        : "Viewer license";
    const price = name.includes("plan") ? calcPrice(data)[0] : data.price;
    const discountedPrice = name.includes("plan")
      ? calcPrice(data)[1]
      : data.discounted_price;

    return (
      <CartItem
        key={`license-${licenseType}`}
        name={name}
        description={licensesInfo[licenseType]?.description}
        price={price}
        discountedPrice={discountedPrice}
        quantity={qty}
        onIncrement={() => handleUpdateQuantity(licenseType, qty + 1)}
        onDecrement={() =>
          handleUpdateQuantity(licenseType, qty > 0 ? qty - 1 : 0)
        }
        currency={currency}
        from={from}
        assignedLicenses={data?.licenses?.total - data?.licenses?.free}
        showChangePlan={licenseType === "plan" && from === "reactivate"}
        handleChangePlan={handleOpenChangePlanModal}
      />
    );
  };

  const { licenseSummaryData, addonSummaryData, vnSummaryData, subTotal } =
    useMemo(() => {
      let runningSubTotal = 0;

      const licenseData =
        selectedLicenses
          ?.filter(
            (l) =>
              l.quantity >= 1 && (l?.type === "plan" || l?.type === "viewer"),
          )
          ?.map((l) => {
            const billingCycle = billingDetails?.plan?.billing_cycle;
            const quantity = Number(l?.quantity ?? 0);
            let name = "";
            let discountedPrice = 0;

            if (l?.type === "plan") {
              name = billingDetails?.plan?.name?.includes("Professional")
                ? `Professional plan`
                : `Standard plan`;
              discountedPrice = calcPrice(billingDetails?.plan)[1];
            } else if (l?.type === "viewer") {
              name = "Viewer license";
              discountedPrice = billingDetails?.viewers?.discounted_price;
            }
            const basePrice = getBasePrice(discountedPrice, billingCycle);
            const { amount, isProrated } =
              from !== "reactivate"
                ? proratedCharge(
                    billingDetails?.start_date,
                    billingDetails?.end_date,
                    basePrice,
                  )
                : { amount: basePrice, isProrated: false };

            runningSubTotal += Number(amount) * quantity;

            return {
              key: `${l.type}`,
              name,
              amount,
              quantity,
              isProrated,
            };
          }) ?? [];

      const addonData =
        selectedLicenses
          ?.filter((l) => {
            if (!(l.quantity >= 1 && l?.type === "feature")) return false;
            const addon = applicableAddons?.find((a) => a.id === l.feature_id);
            if (
              addon?.name === "WHATSAPP BUSINESS" &&
              whatsappAPIStatus !== "ACTIVE"
            )
              return false;
            return true;
          })
          ?.map((l) => {
            const billingCycle = billingDetails?.plan?.billing_cycle;
            const quantity = Number(l?.quantity ?? 0);
            const addon = applicableAddons?.find((a) => a.id === l.feature_id);
            let name =
              addon?.name === "WHATSAPP BUSINESS"
                ? "WhatsApp Business"
                : sentenceCase(addon?.name);
            let discountedPrice = addon?.discounted_price;
            const basePrice = getBasePrice(discountedPrice, billingCycle);

            const { amount, isProrated } =
              from !== "reactivate"
                ? proratedCharge(
                    billingDetails?.start_date,
                    billingDetails?.end_date,
                    basePrice,
                  )
                : { amount: basePrice, isProrated: false };

            runningSubTotal += Number(amount) * quantity;

            return {
              key: `feature-${l.feature_id}`,
              name,
              amount,
              quantity,
              isProrated,
            };
          }) ?? [];

      const vnData =
        from === "reactivate"
          ? (billingDetails?.numbers ?? [])
              ?.filter((vn) => !vn.is_included_in_plan && Number(vn.total) > 0)
              ?.map((vn) => {
                const quantity = Number(vn.total);
                const basePrice = getBasePrice(
                  vn.discounted_price,
                  billingDetails?.plan?.billing_cycle,
                );
                runningSubTotal += basePrice * quantity;
                return {
                  key: `vn-${vn.number_type}-${vn.location}`,
                  name: `${vn.location} - ${formatVnType(vn.number_type)}`,
                  amount: basePrice,
                  quantity,
                  isProrated: false,
                };
              })
          : [];

      return {
        licenseSummaryData: licenseData,
        addonSummaryData: addonData,
        vnSummaryData: vnData,
        subTotal: runningSubTotal,
      };
    }, [
      selectedLicenses,
      billingDetails,
      applicableAddons,
      from,
      whatsappAPIStatus,
    ]);

  const summaryLicenses = licenseSummaryData.map((item) => (
    <SummaryItem
      key={item.key}
      name={item.name}
      amount={item.amount}
      quantity={item.quantity}
      currency={currency}
      isProrated={item.isProrated}
    />
  ));

  const summaryAddons = addonSummaryData.map((item) => (
    <SummaryItem
      key={item.key}
      name={item.name}
      amount={item.amount}
      quantity={item.quantity}
      currency={currency}
      isProrated={item.isProrated}
    />
  ));

  const summaryVns = vnSummaryData.map((item) => (
    <SummaryItem
      key={item.key}
      name={item.name}
      amount={item.amount}
      quantity={item.quantity}
      currency={currency}
      isProrated={item.isProrated}
    />
  ));

  const hasLicenses = licenseSummaryData.length > 0;
  const hasAddons = addonSummaryData.length > 0;
  const hasVns = vnSummaryData.length > 0;
  
  const planQuantity = getQuantity("plan");
  const isBelowMinPlanLicenses =
    from === "reactivate" &&
    planQuantity < 1;

  const taxAmount = currency === "₹" ? subTotal * 0.18 : 0;
  const hasTds = Boolean(
    currency === "₹" &&
      billingDetails?.tds_tax_name &&
      billingDetails?.tds_tax_rate,
  );
  const tdsAmount = hasTds
    ? (Number(billingDetails.tds_tax_rate) * subTotal) / 100
    : 0;
  const totalAmount = subTotal + taxAmount - tdsAmount;

  let isCartEmpty = false;
  if (from === "reactivate") {
    const getFeatureQtyLocal = (addonName) => {
      const addon = applicableAddons?.find((a) => a.name === addonName);
      return addon ? getQuantity("feature", addon.id) : 0;
    };
    isCartEmpty =
      getQuantity("plan") === 0 &&
      getQuantity("viewer") === 0 &&
      getFeatureQtyLocal("BROWSER CALLING") === 0 &&
      getFeatureQtyLocal("AUTODIAL") === 0 &&
      (whatsappAPIStatus !== "ACTIVE" ||
        getFeatureQtyLocal("WHATSAPP BUSINESS") === 0);
  } else {
    isCartEmpty =
      selectedLicenses.filter((l) => Number(l.quantity) > 0).length === 0;
  }

  return (
    <>
      <div className={classes.cartContainer}>
        {showLoader ? (
          <AnimationPage />
        ) : (
          <>
            <div className={classes.cartWrapper}>
              <button onClick={backBtnHandler} className={classes.backBtn}>
                {chevronLeft("20", "20", "#3F3F46")}
                <span className="text-sm medium-font">Back to billing</span>
              </button>
              <h3 className={classes.cartHeader}>Your cart</h3>

              <div className={classes.mainContent}>
                {primaryItem && primaryItem.type === "feature" && (
                  <>
                    <div>
                      {applicableAddons
                        .filter((a) => a.id === primaryItem.feature_id)
                        .map((a) => renderAddonItem(a))}
                    </div>
                    {applicableAddons.filter(
                      (a) => a.id !== primaryItem.feature_id,
                    ).length > 0 && (
                      <div className={classes.sectionWrap}>
                        <h4 className={classes.sectionTitle}>Other add-ons</h4>
                        {applicableAddons
                          .filter((a) => a.id !== primaryItem.feature_id)
                          .map((addon) => renderAddonItem(addon))}
                      </div>
                    )}
                    <div className={classes.sectionWrap}>
                      <h4 className={classes.sectionTitle}>License</h4>
                      {renderLicenseItem("plan", billingDetails?.plan)}
                      {renderLicenseItem("viewer", billingDetails?.viewers)}
                    </div>
                  </>
                )}

                {primaryItem &&
                  (primaryItem.type === "plan" ||
                    primaryItem.type === "viewer") && (
                    <>
                      <div>
                        {primaryItem.type === "plan" &&
                          renderLicenseItem("plan", billingDetails?.plan)}
                        {primaryItem.type === "viewer" &&
                          renderLicenseItem("viewer", billingDetails?.viewers)}
                      </div>
                      <div className={classes.sectionWrap}>
                        <h4 className={classes.sectionTitle}>Other licenses</h4>
                        {primaryItem.type === "plan" &&
                          renderLicenseItem("viewer", billingDetails?.viewers)}
                        {primaryItem.type === "viewer" &&
                          renderLicenseItem("plan", billingDetails?.plan)}
                      </div>
                      {applicableAddons.length > 0 && (
                        <div className={classes.sectionWrap}>
                          <h4 className={classes.sectionTitle}>Add-ons</h4>
                          {applicableAddons.map((addon) =>
                            renderAddonItem(addon),
                          )}
                        </div>
                      )}
                    </>
                  )}

                {from === "reactivate" &&
                  billingDetails?.numbers?.length > 0 && (
                    <div style={{ gap: 0 }} className={classes.sectionWrap}>
                      <h4
                        style={{ marginBottom: "24px" }}
                        className={classes.sectionTitle}
                      >
                        Virtual numbers
                      </h4>
                      <div className={classes.infoBanner}>
                        <div className={classes.bannerIcon}>
                          {infoCircle("#3F3F46", 20, 20)}
                        </div>
                        <p className={classes.bannerText}>
                          Your previously assigned virtual numbers will be
                          reactivated with your plan. If you want to remove a
                          number, please{" "}
                          <span
                            className={classes.bannerLink}
                            onClick={() => {
                              window.HubSpotConversations?.widget?.load({
                                widgetOpen: true,
                              });
                            }}
                          >
                            contact support
                          </span>
                        </p>
                      </div>

                      {/* TODO: not doing this as of now, but might do later */}
                      {/* {billingDetails?.released_numbers_count > 0 && (
                        <div className={classes.warningBanner}>
                          <div className={classes.bannerIcon}>
                            {alertTriangleIcon("#FF9333", 20, 20)}
                          </div>
                          <p className={classes.bannerText}>
                            <span>
                              {billingDetails.released_numbers_count}
                            </span>{" "}
                            of your previous virtual numbers were released due
                            to inactivity. You can purchase new numbers after
                            reactivation.
                          </p>
                        </div>
                      )} */}
                      <div className={classes.vnItems}>
                        {billingDetails.numbers?.map((vn, idx) => (
                          <VirtualNumberItem
                            key={`vn-${idx}`}
                            numberType={vn.number_type}
                            location={vn.location}
                            total={vn.total}
                            discountedPrice={vn.discounted_price}
                            isIncludedInPlan={vn.is_included_in_plan}
                            currency={currency}
                          />
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            <div className={classes.summarySection}>
              <div className={classes.summaryCard}>
                <CustomTooltip
                  placement="top"
                  tooltipTitle={
                    isCartEmpty
                      ? "Cart is empty"
                      : isBelowMinPlanLicenses
                        ? "Minimum 1 plan license is required."
                        : ""
                  }
                  disableHoverListener={!isCartEmpty && !isBelowMinPlanLicenses}
                >
                  <button
                    onClick={handleContinueToPayment}
                    className={classes.continueBtn}
                    disabled={isCartEmpty || isBelowMinPlanLicenses}
                  >
                    Continue to payment
                  </button>
                </CustomTooltip>

                <div className={classes.divider} />
                <h4 className={classes.summaryTitle}>Purchase summary</h4>
                <div className={classes.summaryItemsList}>
                  {summaryLicenses}

                  {hasLicenses && (
                    <>
                      <div className={classes.billingCycleRow}>
                        <span className={classes.billingCycleLabel}>
                          Billing cycle
                        </span>
                        <span className={classes.billingCycleValue}>
                          {sentenceCase(billingDetails?.plan?.billing_cycle)}
                        </span>
                      </div>
                    </>
                  )}

                  {hasLicenses && hasAddons && (
                    <div className={classes.divider} />
                  )}

                  {hasAddons && (
                    <>
                      <h5 className={classes.addOnsHeader}>Add-ons</h5>
                      {summaryAddons}
                    </>
                  )}

                  {hasVns && (
                    <>
                      {(hasLicenses || hasAddons) && (
                        <div className={classes.divider} />
                      )}
                      <h5 className={classes.addOnsHeader}>Virtual numbers</h5>
                      {summaryVns}
                    </>
                  )}
                </div>

                <div className={classes.divider} />

                {currency === "₹" && subTotal > 0 && (
                  <>
                    <div className={classes.totalSection}>
                      <span className={classes.subtotalLabel}>Sub total</span>
                      <span className={classes.subtotalValue}>
                        {currencyFormatter(currency, subTotal)}
                      </span>
                    </div>

                    {hasTds && (
                      <div className={classes.totalSection}>
                        <span className={classes.subtotalLabel}>
                          Amount Withheld (TDS - {billingDetails.tds_tax_name})
                        </span>
                        <span className={classes.subtotalValue}>
                          -{currencyFormatter(currency, tdsAmount)}
                        </span>
                      </div>
                    )}

                    <div>
                      <div className={classes.totalSection}>
                        <span className={classes.subtotalLabel}>Tax (18%)</span>
                        <span className={classes.subtotalValue}>
                          {currencyFormatter(currency, taxAmount)}
                        </span>
                      </div>
                      <p className={classes.noGstNote}>
                        * When using credits for payment, GST will not be included.
                      </p>
                    </div>

                    <div className={classes.divider} />
                  </>
                )}

                <div className={classes.totalSection}>
                  <span className={classes.totalLabel}>Total</span>
                  <span className={classes.totalLabel}>
                    {currencyFormatter(currency, totalAmount)}
                  </span>
                </div>
              </div>

              <div className={classes.financeCard}>
                <h4 className={classes.financeTitle}>Need finance approval?</h4>
                <p className={classes.financeDesc}>
                  Download a quote PDF to share with your finance team. Valid
                  for 30 days.
                </p>
                <button
                  onClick={handleDownloadProformaInvoice}
                  className={classes.downloadBtn}
                  disabled={isCartEmpty || isBelowMinPlanLicenses}
                >
                  {showLoader ? (
                    <div className={classes.loader}>
                      <div className={classes.spinner}></div>
                    </div>
                  ) : (
                    fileIcon("#3F3F46", 20, 20)
                  )}
                  Download quote (PDF)
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {showChoosePlanModal && (
        <ChoosePlanModal
          visible={showChoosePlanModal}
          handleCloseModal={() => setShowChoosePlanModal(false)}
          currentBillingCycle={sentenceCase(
            billingDetails?.plan?.billing_cycle,
          )}
          currentType={
            billingDetails?.plan?.name?.toLowerCase().includes("professional")
              ? "PROFESSIONAL"
              : "STANDARD"
          }
          changePlanHandler={changePlanHandler}
          plansData={plansData}
        />
      )}
      {paymentMethodModalVisible && (
        <PaymentMethodModal
          visible={paymentMethodModalVisible}
          handleClose={() => setPaymentMethodModalVisible(false)}
          handlePayWithCredits={() => purchaseLicenseHandler("credits")}
          handlePayWithCard={() => purchaseLicenseHandler("card")}
        />
      )}
      {insufficientCreditsModalVisible && (
        <InsufficientCreditsModal
          visible={insufficientCreditsModalVisible}
          handleClose={() => setInsufficientCreditsModalVisible(false)}
          handlePayWithCard={() => purchaseLicenseHandler("card")}
        />
      )}
    </>
  );
};

export default Cart;

const SummaryItem = ({ name, amount, quantity, currency, isProrated }) => {
  return (
    <div className={classes.summaryItem}>
      <div className={classes.itemInfo}>
        <span className={classes.itemName}>{name}</span>
        <span className={classes.itemPrice}>
          {currencyFormatter(currency, quantity * amount)}
        </span>
      </div>
      <div className={classes.itemInfo}>
        <span className={classes.itemDetails}>
          {currencyFormatter(currency, amount)} x {quantity} license
          {quantity !== 1 ? "s" : ""}
        </span>
        {isProrated && (
          <span className={classes.itemDetails}>Proration applied</span>
        )}
      </div>
    </div>
  );
};

const VirtualNumberItem = ({
  numberType,
  location,
  total,
  discountedPrice,
  isIncludedInPlan,
  currency,
}) => {
  return (
    <div className={classes.vnItem}>
      <div className={classes.vnInfo}>
        <p className={classes.vnName}>
          {location} - {formatVnType(numberType)}
        </p>
        <div className={classes.vnPriceLine}>
          <span className={classes.vnPriceText}>
            {currencyFormatter(currency, discountedPrice)}
            <span className={classes.vnUnitText}>/number/month</span>
          </span>
          {isIncludedInPlan && (
            <span className={classes.vnIncludedTag}>Included with plan</span>
          )}
        </div>
      </div>
      <div className={classes.vnItemRight}>
        <span className={classes.vnQtyLabel}>Qty</span>
        <div className={classes.vnQtyBox}>{total}</div>
      </div>
    </div>
  );
};
